import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { bdcApi } from "../api/bdc";

/**
 * rooftopId → IANA timezone resolver, shared by every surface that
 * renders an appointment time. Appointment times are STORE times: the
 * wall clock the customer was promised lives in the appointment
 * rooftop's zone, never the viewer's browser zone. Backed by the same
 * ["bdc","rooftops"] query the rest of the app uses, so it costs no
 * extra request. Unknown/missing rooftop falls back to the first
 * rooftop's zone (single-store dealerships), then the browser's.
 */
export function useRooftopZone(): (rooftopId: string | null | undefined) => string {
  const rooftopsQuery = useQuery({ queryKey: ["bdc", "rooftops"], queryFn: bdcApi.rooftops });
  const rooftops = rooftopsQuery.data;
  return useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rooftops ?? []) map.set(r.id, r.timezone);
    const fallback =
      rooftops?.[0]?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (rooftopId) => (rooftopId ? (map.get(rooftopId) ?? fallback) : fallback);
  }, [rooftops]);
}
